/**
 * <anear-workflow> — generic, declarative trigger for ANAPI-orchestrated
 * workflows (Workflow Execution feature).
 *
 * An AppM renders this element to kick off a named, async, status-tracked
 * server workflow (e.g. "video_transcode", later "pdf_placard", montage
 * assembly, ...) WITHOUT touching AWS, developer API keys, or bespoke
 * endpoints. ABR performs the participant-scoped ANAPI call with the ambient
 * credentialed client — the same participant-scoped call the capture widgets
 * use for uploads. Adding a future workflow requires NO change here.
 *
 * Usage (headless, fire-and-forget — the common case):
 *   <anear-workflow
 *     workflow="video_transcode"
 *     input='{"input_key":"event_media_uploads/…/clip.mov","purpose":"tribute_video"}'
 *     auto>
 *   </anear-workflow>
 *
 * Usage (poll to surface readiness, e.g. for a playback surface):
 *   <anear-workflow workflow="video_transcode" input='…' auto poll></anear-workflow>
 *   // emits "anear-workflow:ready" / "anear-workflow:failed" CustomEvents
 *
 * Attributes:
 *   workflow      - registered workflow name (required to trigger)
 *   input         - JSON object of workflow input. event_id is auto-injected
 *                   from the ambient event if omitted. A single JSON attribute
 *                   keeps the sanitizer generic across all current/future
 *                   workflows.
 *   auto          - trigger on connect (boolean attribute)
 *   poll          - after triggering (or with run-id), poll status until
 *                   ready/failed (boolean attribute)
 *   poll-interval - ms between polls (default 4000, min 1500)
 *   run-id        - poll an existing run instead of triggering a new one
 *   show-status   - render a small status line (otherwise headless)
 *
 * Server-side idempotency (find-or-create per workflow) makes re-triggering on
 * ABR rerenders/remounts harmless; we also guard against double-trigger within
 * a single element instance.
 */

const DEFAULT_POLL_INTERVAL_MS = 4000
const MIN_POLL_INTERVAL_MS = 1500
const TERMINAL_STATUSES = new Set(['ready', 'failed'])

function parseIntAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseInt(val, 10)
  return Number.isFinite(n) ? n : fallback
}

class AnearWorkflow extends HTMLElement {
  constructor() {
    super()
    this._start = null
    this._poll = null
    this._eventId = null

    this._runId = null
    this._status = null
    this._triggered = false
    this._pollTimer = null
    this._destroyed = false

    this._statusEl = null
  }

  static get observedAttributes() {
    return ['show-status']
  }

  connectedCallback() {
    this.render()
    // If wired before connect, kick off immediately; otherwise initialize() does it.
    if (this._start) this.maybeStart()
  }

  disconnectedCallback() {
    this._destroyed = true
    this.clearPollTimer()
  }

  // Injected by ABR (useAppDisplayDomSetup): start/poll bound to the ambient
  // credentialed AnearApi client, plus the current event id.
  initialize({ start, poll, eventId } = {}) {
    this._start = start || null
    this._poll = poll || null
    this._eventId = eventId || null
    this.maybeStart()
  }

  get workflowName() {
    return this.getAttribute('workflow') || ''
  }

  get isAuto() {
    return this.hasAttribute('auto')
  }

  get isPoll() {
    return this.hasAttribute('poll')
  }

  get pollIntervalMs() {
    return Math.max(MIN_POLL_INTERVAL_MS, parseIntAttr(this.getAttribute('poll-interval'), DEFAULT_POLL_INTERVAL_MS))
  }

  get showStatus() {
    return this.hasAttribute('show-status')
  }

  parseInput() {
    const raw = this.getAttribute('input')
    let input = {}
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) input = parsed
      } catch (_e) {
        this.setStatus('Invalid workflow input', true)
        return null
      }
    }
    // Auto-inject the ambient event id so AppM templates don't have to know it.
    if (this._eventId && input.event_id == null) input.event_id = this._eventId
    return input
  }

  async maybeStart() {
    if (this._destroyed) return

    // Poll-only mode: a run id was supplied, just track it.
    const existingRunId = this.getAttribute('run-id')
    if (existingRunId && !this._runId) {
      this._runId = existingRunId
      if (this.isPoll) this.startPolling()
      return
    }

    if (!this.isAuto) return
    if (this._triggered) return
    if (!this._start || !this.workflowName) return

    const input = this.parseInput()
    if (input === null) return

    this._triggered = true
    this.setStatus('Starting…')
    try {
      const attrs = await this._start({ workflow: this.workflowName, input })
      if (this._destroyed) return
      this._runId = attrs.run_id || attrs.runId || null
      this.applyStatus(attrs)
      this.emit('started', attrs)
      if (this.isPoll && this._runId && !TERMINAL_STATUSES.has(this._status)) {
        this.startPolling()
      }
    } catch (error) {
      if (this._destroyed) return
      // Allow a retry on a later rerender if the trigger failed outright.
      this._triggered = false
      this.setStatus(error?.message || 'Could not start workflow', true)
      this.emit('error', { error: error?.message || 'start_failed' })
    }
  }

  startPolling() {
    this.clearPollTimer()
    if (!this._poll || !this._runId || !this.workflowName) return

    const tick = async () => {
      if (this._destroyed) return
      try {
        const attrs = await this._poll({ runId: this._runId, workflow: this.workflowName })
        if (this._destroyed) return
        this.applyStatus(attrs)
        if (TERMINAL_STATUSES.has(this._status)) {
          this.clearPollTimer()
          this.emit(this._status === 'ready' ? 'ready' : 'failed', attrs)
          return
        }
      } catch (error) {
        // Transient poll errors are non-fatal; keep trying until disconnected.
      }
      this._pollTimer = window.setTimeout(tick, this.pollIntervalMs)
    }

    this._pollTimer = window.setTimeout(tick, this.pollIntervalMs)
  }

  clearPollTimer() {
    if (this._pollTimer) {
      window.clearTimeout(this._pollTimer)
      this._pollTimer = null
    }
  }

  applyStatus(attrs) {
    this._status = attrs?.status || this._status
    if (this._runId) this.setAttribute('run-id', this._runId)
    if (this._status) this.setAttribute('status', this._status)
    const message = attrs?.status_message || (this._status ? `Status: ${this._status}` : '')
    this.setStatus(message)
  }

  emit(kind, detail) {
    this.dispatchEvent(new CustomEvent(`anear-workflow:${kind}`, {
      bubbles: true,
      detail: { workflow: this.workflowName, runId: this._runId, ...detail }
    }))
  }

  setStatus(text, isError = false) {
    if (!this._statusEl) return
    this._statusEl.textContent = text || ''
    this._statusEl.style.color = isError ? '#b3261e' : '#5c6370'
  }

  render() {
    if (this._statusEl) return
    if (!this.showStatus) return // headless by default

    this._statusEl = document.createElement('div')
    this._statusEl.className = 'anear-workflow-status'
    this.appendChild(this._statusEl)
  }
}

if (!customElements.get('anear-workflow')) {
  customElements.define('anear-workflow', AnearWorkflow)
}

export default AnearWorkflow
